import type { CourseModule } from '@/types/lesson';

/**
 * Модуль 2: Базовые команды Git
 */
const module2: CourseModule = {
  id: 'module-2',
  title: 'Базовые команды Git',
  slug: 'basic-git-commands',
  order: 2,
  lessons: [
    {
      id: '2-1',
      moduleId: 'module-2',
      title: 'Создание коммитов',
      slug: 'making-commits',
      order: 1,
      theory: `# Создание коммитов

Коммит — это снимок вашего проекта в определённый момент времени.

:::diagram commit-structure:::

## История концепции коммита

### Эволюция подходов к сохранению изменений:

| Система | Подход | Проблема |
|---------|--------|----------|
| RCS | Версии файлов | Нет истории проекта |
| CVS | Файлы + метки | Нет атомарности |
| Subversion | Номер ревизии | Централизованно |
| **Git** | **Коммит = снимок** | **Распределённо** |

### Почему Git использует снимки (snapshots)?

В отличие от систем, хранящих diff'ы (изменения), Git хранит полное состояние каждого файла:
- **Быстрое переключение** между версиями
- **Целостность данных** — каждый файл имеет хеш
- **Эффективность** — одинаковые файлы хранятся один раз

## Команда commit

\`\`\`bash
# Базовый коммит с сообщением
git commit -m "Ваше сообщение коммита"

# Коммит с открытием редактора для многострочного сообщения
git commit

# Коммит с добавлением всех изменённых файлов
git commit -a -m "Сообщение"

# Коммит с указанием автора
git commit --author="Имя <email@example.com>" -m "Сообщение"
\`\`\`

## Глубокое погружение: Внутреннее устройство коммита

### Структура коммита в Git

\`\`\`
Commit Object (объект коммита)
┌─────────────────────────────────────────────┐
│ tree: a1b2c3d...    ← ссылка на дерево файлов │
│ parent: e4f5g6h...  ← ссылка на родителя      │
│ author: Иван <ivan@mail.ru> 1234567890 +0300 │
│ committer: Иван <ivan@mail.ru> 1234567890 +0300 │
│                                              │
│ Добавить функцию аутентификации             │
│                                              │
│ - Создан модуль auth.ts                      │
│ - Добавлены тесты                           │
└─────────────────────────────────────────────┘
        │
        ▼
Tree Object (дерево файлов)
┌─────────────────────────────────────────────┐
│ 100644 blob a1b2c3d...  README.md           │
│ 100644 blob e4f5g6h...  src/auth.ts         │
│ 040000 tree i7j8k9l...  src/                │
└─────────────────────────────────────────────┘
        │
        ▼
Blob Objects (содержимое файлов)
┌─────────────────────────────────────────────┐
│ // auth.ts                                   │
│ export function authenticate() { ... }       │
└─────────────────────────────────────────────┘
\`\`\`

### Как создаётся коммит

1. **git add** создаёт blob-объекты для каждого файла
2. **git commit** создаёт tree-объект для директории
3. Создаётся commit-объект с метаданными
4. HEAD обновляется на новый коммит

### Просмотр внутренностей коммита

\`\`\`bash
# Посмотреть содержимое коммита
git cat-file -p HEAD

# Посмотреть дерево коммита
git cat-file -p HEAD^{tree}

# Посмотреть тип объекта
git cat-file -t HEAD
# commit

# Посмотреть размер объекта
git cat-file -s HEAD
# 287 (байт)
\`\`\`

## Хорошие сообщения коммитов

### Структура сообщения

\`\`\`
<заголовок> (50 символов максимум)

<пустая строка>

<тело сообщения> (72 символа в строке максимум)

<пустая строка>

<footer> (ссылки на issues, breaking changes)
\`\`\`

### Пример хорошего сообщения

\`\`\`
Добавить двухфакторную аутентификацию

Реализована поддержка TOTP (Time-based One-Time Password):
- Генерация QR-кода для настройки
- Проверка кодов с окном в 30 секунд
- Резервные коды для восстановления

Closes #123
Breaking change: API /login теперь возвращает QR-код
\`\`\`

### Семь правил хороших сообщений (Chris Beams)

1. **Отделяйте заголовок от тела пустой строкой**
2. **Ограничьте заголовок 50 символами**
3. **Пишите заголовок с заглавной буквы**
4. **Не ставьте точку в конце заголовка**
5. **Используйте повелительное наклонение** ("Добавить", а не "Добавил")
6. **Ограничьте тело 72 символами в строке**
7. **В теле объясняйте что и почему, а не как**

### Почему повелительное наклонение?

Git сам использует этот стиль:
\`\`\`
$ git merge branch
Merge branch 'feature'
\`\`\`

Представьте: "Если применить этот коммит, он **добавит** функцию"

### Conventional Commits

Стандартизированный формат сообщений:

\`\`\`
<тип>(<область>): <описание>

[необязательное тело]

[необязательный footer]
\`\`\`

**Типы:**
- \`feat\` — новая функция
- \`fix\` — исправление бага
- \`docs\` — документация
- \`style\` — форматирование
- \`refactor\` — рефакторинг
- \`test\` — тесты
- \`chore\` — рутинные задачи

**Примеры:**
\`\`\`
feat(auth): добавить двухфакторную аутентификацию
fix(api): исправить утечку памяти при запросах
docs(readme): обновить инструкции по установке
refactor(utils): упростить логику валидации
\`\`\`

## Структура коммита

Каждый коммит содержит:
- **SHA-1 хеш** — уникальный идентификатор (40 символов)
- **Tree** — ссылка на корневое дерево файлов
- **Parent(s)** — ссылка на родительский коммит(ы)
- **Author** — кто написал код
- **Committer** — кто применил коммит
- **Message** — описание изменений

### Вычисление SHA-1

\`\`\`bash
# SHA-1 вычисляется от содержимого коммита
echo -e "commit <размер>\\0<содержимое>" | shasum

# Пример
git cat-file commit HEAD | shasum
# a1b2c3d4e5f6...  -
\`\`\`

Любое изменение в коммите меняет его хеш!

## Изменение последнего коммита

\`\`\`bash
# Изменить сообщение последнего коммита
git commit --amend -m "Новое сообщение"

# Добавить забытый файл в последний коммит
git add forgotten-file.txt
git commit --amend --no-edit

# Изменить автора последнего коммита
git commit --amend --author="Имя <email@example.com>"

# Редактировать содержимое (откроет редактор)
git commit --amend
\`\`\`

### Важно: amend создаёт новый коммит!

\`\`\`
До amend:
A --- B (HEAD)

После amend:
A --- B' (HEAD)
      \\
       B (оригинальный, потерян)
\`\`\`

Оригинальный коммит B остаётся в reflog и будет удалён через 90 дней.

## Практические кейсы

### Кейс 1: "Забыл добавить файл в коммит"

\`\`\`bash
# Сделали коммит
git commit -m "Добавить функцию входа"

# Ой, забыли тесты!
git add tests/login.test.ts
git commit --amend --no-edit

# Теперь тесты включены в коммит
\`\`\`

### Кейс 2: "Опечатка в сообщении коммита"

\`\`\`bash
# Сообщение с ошибкой
git commit -m "Добавить фунцию входа"  # опечатка!

# Исправляем
git commit --amend -m "Добавить функцию входа"
\`\`\`

### Кейс 3: "Нужно разделить изменения на несколько коммитов"

\`\`\`bash
# Изменили 3 файла: багфикс, функция, документация
git status
# modified: src/auth.ts (багфикс)
# modified: src/feature.ts (функция)
# modified: README.md (документация)

# Коммит 1: Багфикс
git add src/auth.ts
git commit -m "fix(auth): исправить проверку токена"

# Коммит 2: Функция
git add src/feature.ts
git commit -m "feat: добавить экспорт данных"

# Коммит 3: Документация
git add README.md
git commit -m "docs: обновить README"
\`\`\`

### Кейс 4: "Пустой коммит для документации"

\`\`\`bash
# Иногда нужен коммит без изменений (например, для триггера CI)
git commit --allow-empty -m "chore: триггер CI"
\`\`\`

## Типичные ошибки

### Ошибка: "Nothing to commit"

\`\`\`bash
$ git commit -m "Сообщение"
nothing to commit, clean working directory
\`\`\`

**Причина**: Нет изменений в индексе
**Решение**: Сначала \`git add\` или используйте \`git commit -a\`

### Ошибка: "Please tell me who you are"

\`\`\`bash
$ git commit -m "Сообщение"
*** Please tell me who you are.
\`\`\`

**Причина**: Не настроены user.name и user.email
**Решение**:
\`\`\`bash
git config --global user.name "Ваше имя"
git config --global user.email "ваш@email.com"
\`\`\`

### Ошибка: "Amend после push"

\`\`\`bash
# Сделали amend после push
git commit --amend -m "Новое сообщение"
git push
# ! [rejected] main -> main (non-fast-forward)
\`\`\`

**Причина**: Локальная история расходится с удалённой
**Решение**: \`git push --force-with-lease\` (осторожно!)

## Связь с другими концепциями

- **git add** — добавляет файлы в индекс перед коммитом
- **git reset** — отменяет коммиты
- **git rebase** — изменяет историю коммитов
- **git cherry-pick** — копирует коммиты
- **git revert** — создаёт обратный коммит
`,
      steps: [
        {
          id: 1,
          title: 'Создание коммита',
          description: 'Создайте коммит с сообщением "Initial commit".',
          expectedCommand: 'git commit -m "Initial commit"',
          validationRegex: 'git commit\\s+(-m|--message)\\s+["\'].*["\']',
          hint: {
            level1: 'Сохраните изменения в истории.',
            level2: 'Используйте git commit -m "Initial commit".',
            level3: 'Введите: git commit -m "Initial commit"',
          },
        },
      ],
      keyInsight: {
        main: 'Коммиты — это строительные блоки истории Git. Пишите ясные, описательные сообщения.',
        example:
          'Хорошее сообщение коммита: "Добавить валидацию email при регистрации" — сразу понятно, что изменилось. Плохое: "fix" или "update" — непонятно, что было сделано.',
        warning:
          'Избегайте коммитов с бессмысленными сообщениями типа "asdf", "fix again", "WIP". Через месяц никто (включая вас) не поймёт, что делает этот коммит.',
        tip: 'Используйте повелительное наклонение в сообщениях: "Добавить функцию", а не "Добавлена функция". Это стандарт Git — представьте, что команда скажет "Если применить этот коммит, он..."',
        additional: [
          'Каждый коммит имеет уникальный SHA-1 хеш, который служит его идентификатором.',
          'Коммит содержит снимок (snapshot) всех файлов, а не только изменения.',
          'Хорошая практика: один коммит — одна логическая единица изменений.',
        ],
      },
      estimatedTime: 15,
      quizQuestions: [
        {
          id: '2-1-q1',
          question: 'Какое наклонение следует использовать в сообщениях коммитов?',
          options: [
            {
              id: 'a',
              text: 'Прошедшее ("Добавил функцию")',
              isCorrect: false,
              explanation: 'Неправильно. Прошедшее время не соответствует стилю Git.',
            },
            {
              id: 'b',
              text: 'Повелительное ("Добавить функцию")',
              isCorrect: true,
              explanation:
                'Правильно! Git использует повелительное наклонение: "Если применить этот коммит, он добавит функцию".',
            },
            {
              id: 'c',
              text: 'Настоящее ("Добавляю функцию")',
              isCorrect: false,
              explanation: 'Неправильно. Настоящее время не является стандартом Git.',
            },
            {
              id: 'd',
              text: 'Будущее ("Будет добавлена функция")',
              isCorrect: false,
              explanation: 'Неправильно. Будущее время не используется в сообщениях коммитов.',
            },
          ],
          explanation:
            'Стандарт Git — повелительное наклонение в сообщениях коммитов. Это соответствует стилю сообщений, которые Git генерирует автоматически (например, "Merge branch").',
          difficulty: 'easy',
        },
        {
          id: '2-1-q2',
          question: 'Какая команда изменяет сообщение последнего коммита?',
          options: [
            {
              id: 'a',
              text: 'git commit -m "Новое сообщение"',
              isCorrect: false,
              explanation:
                'Неправильно. Эта команда создаёт новый коммит, а не изменяет существующий.',
            },
            {
              id: 'b',
              text: 'git commit --amend -m "Новое сообщение"',
              isCorrect: true,
              explanation: 'Правильно! --amend изменяет последний коммит.',
            },
            {
              id: 'c',
              text: 'git rebase -i',
              isCorrect: false,
              explanation:
                'Неправильно. rebase используется для изменения истории, но не так просто для последнего коммита.',
            },
            {
              id: 'd',
              text: 'git edit commit',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды не существует.',
            },
          ],
          explanation:
            'git commit --amend позволяет изменить сообщение последнего коммита или добавить забытые файлы. Важно: это создаёт новый коммит с другим хешем!',
          difficulty: 'easy',
        },
        {
          id: '2-1-q3',
          question: 'Что такое Conventional Commits?',
          options: [
            {
              id: 'a',
              text: 'Новый формат файлов Git',
              isCorrect: false,
              explanation: 'Неправильно. Conventional Commits — это спецификация для сообщений.',
            },
            {
              id: 'b',
              text: 'Спецификация для форматирования сообщений коммитов',
              isCorrect: true,
              explanation:
                'Правильно! Conventional Commits определяет формат: тип(область): описание.',
            },
            {
              id: 'c',
              text: 'Инструмент для автоматического коммита',
              isCorrect: false,
              explanation: 'Неправильно. Это не автоматический инструмент.',
            },
            {
              id: 'd',
              text: 'Новая версия Git',
              isCorrect: false,
              explanation: 'Неправильно. Conventional Commits — это соглашение, а не версия Git.',
            },
          ],
          explanation:
            'Conventional Commits — это спецификация для форматирования сообщений коммитов. Формат: тип(область): описание. Примеры: feat(auth): добавить вход, fix(api): исправить баг.',
          difficulty: 'medium',
        },
        {
          id: '2-1-q4',
          question: 'Какой тип Conventional Commit используется для исправления бага?',
          options: [
            {
              id: 'a',
              text: 'feat',
              isCorrect: false,
              explanation: 'Неправильно. feat используется для новых функций.',
            },
            {
              id: 'b',
              text: 'fix',
              isCorrect: true,
              explanation: 'Правильно! fix — это тип для исправления багов.',
            },
            {
              id: 'c',
              text: 'bug',
              isCorrect: false,
              explanation: 'Неправильно. bug не является стандартным типом Conventional Commits.',
            },
            {
              id: 'd',
              text: 'patch',
              isCorrect: false,
              explanation: 'Неправильно. patch не является стандартным типом Conventional Commits.',
            },
          ],
          explanation:
            'В Conventional Commits тип fix используется для исправления багов, feat — для новых функций, docs — для документации, refactor — для рефакторинга.',
          difficulty: 'easy',
        },
      ],
      resources: [
        {
          title: 'Git Documentation: git commit',
          url: 'https://git-scm.com/docs/git-commit',
          type: 'documentation',
          description: 'Официальная документация о команде commit',
        },
        {
          title: 'Conventional Commits',
          url: 'https://www.conventionalcommits.org/',
          type: 'article',
          description: 'Спецификация для форматирования сообщений коммитов',
        },
        {
          title: 'How to Write a Git Commit Message',
          url: 'https://cbea.ms/git-commit/',
          type: 'article',
          description: 'Лучшие практики написания сообщений коммитов от Chris Beams',
        },
      ],
    },
    {
      id: '2-2',
      moduleId: 'module-2',
      title: 'Просмотр истории',
      slug: 'viewing-history',
      order: 2,
      theory: `# Просмотр истории

Git предоставляет мощные инструменты для изучения истории вашего проекта.

:::diagram commit-history:::

## История концепции лога

### Эволюция просмотра истории:

| Система | Подход | Ограничения |
|---------|--------|-------------|
| RCS | Только локальные версии | Нет общей истории |
| CVS | Лог по файлам | Нет атомарности |
| Subversion | \`svn log\` | Централизованно, медленно |
| **Git** | **git log** | **Распределённо, быстро** |

### Почему git log работает локально?

В отличие от централизованных систем, Git хранит всю историю локально:
- **Скорость**: Нет сетевых запросов
- **Офлайн**: Работает без интернета
- **Гибкость**: Мощные фильтры и форматы

## Базовый лог

\`\`\`bash
git log
\`\`\`

Показывает:
- **Хеш коммита** (SHA-1)
- **Автора** (имя и email)
- **Дату** (временная метка)
- **Сообщение** (описание изменений)

### Подробный анализ вывода

\`\`\`bash
$ git log
commit a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0 (HEAD -> main)
Author: Иван Петров <ivan@example.com>
Date:   Mon Jan 15 10:30:00 2024 +0300

    Добавить функцию аутентификации
    
    - Создан модуль auth.ts
    - Добавлены тесты
    - Обновлена документация

commit e4f5g6h7i8j9k0l1m2n3o4p5q6r7s8t9u0v1w2x3
Author: Мария Сидорова <maria@example.com>
Date:   Sun Jan 14 16:45:00 2024 +0300

    Исправить ошибку валидации
\`\`\`

## Компактный вид

\`\`\`bash
# Одна строка на коммит
git log --oneline

# Пример вывода:
# a1b2c3d Добавить функцию аутентификации
# e4f5g6h Исправить ошибку валидации
# i7j8k9l Начальный коммит
\`\`\`

### С графом ветвлений

\`\`\`bash
# Визуализация веток
git log --oneline --graph

# Пример вывода:
# * a1b2c3d (HEAD -> main) Добавить функцию аутентификации
# * e4f5g6h Исправить ошибку валидации
# |\\
# | * i7j8k9l (feature/login) Начать разработку входа
# |/
# * m3n4o5p Начальный коммит
\`\`\`

### Все ветки

\`\`\`bash
# Показать все ветки (включая удалённые)
git log --oneline --graph --all

# С декорациями (имена веток и тегов)
git log --oneline --graph --all --decorate
\`\`\`

## Глубокое погружение: Форматирование вывода

### Кастомный формат

\`\`\`bash
# Сокращённый хеш + автор + сообщение
git log --pretty=format:"%h - %an : %s"

# Пример:
# a1b2c3d - Иван Петров : Добавить функцию аутентификации
# e4f5g6h - Мария Сидорова : Исправить ошибку валидации
\`\`\`

### Спецификаторы формата

| Спецификатор | Значение |
|--------------|----------|
| \`%H\` | Полный хеш коммита |
| \`%h\` | Сокращённый хеш |
| \`%an\` | Имя автора |
| \`%ae\` | Email автора |
| \`%ad\` | Дата автора |
| \`%s\` | Сообщение (тема) |
| \`%b\` | Тело сообщения |
| \`%d\` | Декорации (ветки, теги) |

### Готовые форматы

\`\`\`bash
# medium (по умолчанию)
git log --pretty=medium

# short
git log --pretty=short

# full
git log --pretty=full

# fuller
git log --pretty=fuller

# email
git log --pretty=email
\`\`\`

## Фильтрация истории

### По количеству

\`\`\`bash
# Последние N коммитов
git log -n 5
git log -5  # сокращённая форма

# Все коммиты после указанного
git log HEAD~5..HEAD

# Диапазон коммитов
git log commit1..commit2
\`\`\`

### По автору

\`\`\`bash
# По имени автора
git log --author="Иван"

# По email
git log --author="ivan@example.com"

# Регулярное выражение
git log --author="Иван|Мария"
\`\`\`

### По содержимому

\`\`\`bash
# По сообщению коммита
git log --grep="баг"

# По содержимому изменений (S - pickaxe)
git log -S "function login"

# По регулярному выражению в diff
git log -G "function.*login"

# По добавленным/удалённым строкам
git log -S "console.log" --all
\`\`\`

### По файлам

\`\`\`bash
# История конкретного файла
git log -- filename.txt

# История с diff'ами
git log -p -- filename.txt

# История переименований
git log --follow -- renamed-file.txt

# История конкретных строк
git log -L 10,20:filename.txt
git log -L :functionName:filename.txt
\`\`\`

### По дате

\`\`\`bash
# После даты
git log --since="2024-01-01"

# До даты
git log --until="2024-12-31"

# Относительные даты
git log --since="2 weeks ago"
git log --since="1 month ago"
git log --after="yesterday"

# Диапазон
git log --since="2024-01-01" --until="2024-01-31"
\`\`\`

### Комбинированные фильтры

\`\`\`bash
# Коммиты Ивана за последнюю неделю
git log --author="Иван" --since="1 week ago"

# Коммиты с "fix" в сообщении в конкретном файле
git log --grep="fix" -- src/auth.ts

# Коммиты Ивана или Марии
git log --author="Иван" --author="Мария"
\`\`\`

## Просмотр конкретного коммита

\`\`\`bash
# Показать детали коммита
git show <хеш-коммита>

# Показать конкретный файл в коммите
git show <хеш-коммита>:filename.txt

# Показать только изменения
git show --stat <хеш-коммита>

# Показать только сообщение
git show --no-patch <хеш-коммита>
\`\`\`

### Пример вывода git show

\`\`\`bash
$ git show a1b2c3d
commit a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0
Author: Иван Петров <ivan@example.com>
Date:   Mon Jan 15 10:30:00 2024 +0300

    Добавить функцию аутентификации

diff --git a/src/auth.ts b/src/auth.ts
new file mode 100644
index 0000000..e4f5g6h
--- /dev/null
+++ b/src/auth.ts
@@ -0,0 +1,15 @@
+export function authenticate(user, password) {
+  // Валидация
+  if (!user || !password) {
+    return false;
+  }
+  // Проверка credentials
+  return validate(user, password);
+}
\`\`\`

## Статистика изменений

\`\`\`bash
# Статистика по каждому коммиту
git log --stat

# Пример:
# a1b2c3d Добавить функцию аутентификации
#  src/auth.ts      | 15 +++++++++++++++
#  src/index.ts     |  3 ++-
#  2 files changed, 17 insertions(+), 1 deletion(-)
\`\`\`

### Краткая статистика

\`\`\`bash
# Только числа
git log --shortstat

# Пример:
# 2 files changed, 17 insertions(+), 1 deletion(-)
\`\`\`

## Поиск изменений

### git blame — кто изменил строку

\`\`\`bash
# Показать, кто и когда изменил каждую строку
git blame filename.txt

# Пример:
# a1b2c3d4 (Иван Петров 2024-01-15 10:30:00 +0300  1) export function authenticate(
# e4f5g6h7 (Мария Сидорова 2024-01-14 16:45:00 +0300  2)   user: string,
# a1b2c3d4 (Иван Петров 2024-01-15 10:30:00 +0300  3)   password: string
# a1b2c3d4 (Иван Петров 2024-01-15 10:30:00 +0300  4) ): boolean {
\`\`\`

### Опции blame

\`\`\`bash
# Показать email автора
git blame -e filename.txt

# Показать только строки 10-20
git blame -L 10,20 filename.txt

# Показать изменения после коммита
git blame -L :functionName filename.txt
\`\`\`

### git bisect — бинарный поиск бага

\`\`\`bash
# Начать поиск
git bisect start

# Указать плохой коммит (с багом)
git bisect bad HEAD

# Указать хороший коммит (без бага)
git bisect good v1.0.0

# Git автоматически найдёт коммит с багом
# После каждого шага отмечайте:
git bisect good  # если бага нет
git bisect bad   # если баг есть

# Завершить поиск
git bisect reset
\`\`\`

## Практические кейсы

### Кейс 1: "Найти, когда появился баг"

\`\`\`bash
# Найти коммит, добавивший строку
git log -S "buggyFunction" --all

# Найти коммит, изменивший конкретную строку
git log -L 42,42:src/app.ts

# Использовать bisect для поиска
git bisect start
git bisect bad HEAD
git bisect good v1.0.0
\`\`\`

### Кейс 2: "Посмотреть историю файла"

\`\`\`bash
# Полная история файла
git log --follow -p -- src/components/Button.tsx

# Кто изменил файл
git shortlog -n -- src/components/Button.tsx

# Последние изменения
git log -3 -- src/components/Button.tsx
\`\`\`

### Кейс 3: "Статистика по авторам"

\`\`\`bash
# Количество коммитов по авторам
git shortlog -sn

# Пример:
#    42  Иван Петров
#    28  Мария Сидорова
#    15  Алексей Козлов

# Статистика по строкам
git log --author="Иван" --pretty=tformat: --numstat | awk '{add+=$1; del+=$2} END {print "added:", add, "deleted:", del}'
\`\`\`

### Кейс 4: "История за период"

\`\`\`bash
# Коммиты за сегодня
git log --since="today"

# Коммиты за неделю
git log --since="1 week ago" --oneline

# Коммиты за месяц по авторам
git log --since="1 month ago" --format="%an" | sort | uniq -c | sort -rn
\`\`\`

## Типичные ошибки

### Ошибка: "Слишком много вывода"

\`\`\`bash
# Проблема: тысячи коммитов
git log

# Решение: ограничить вывод
git log -n 20
git log --since="1 month ago"
\`\`\`

### Ошибка: "Не вижу удалённые ветки"

\`\`\`bash
# Проблема: git log показывает только текущую ветку
git log

# Решение: показать все ветки
git log --all
git log --graph --all --oneline
\`\`\`

### Ошибка: "Не вижу файл, который переименовали"

\`\`\`bash
# Проблема: история обрывается при переименовании
git log -- old-name.ts

# Решение: отслеживать переименования
git log --follow -- new-name.ts
\`\`\`

## Полезные алиасы

\`\`\`bash
# Красивый граф
git config --global alias.lg "log --graph --pretty=format:'%Cred%h%Creset -%C(yellow)%d%Creset %s %Cgreen(%cr) %C(bold blue)<%an>%Creset' --abbrev-commit"

# Использование:
git lg

# История файла
git config --global alias.filelog "log --follow -p"

# Последние коммиты
git config --global alias.last "log -1 HEAD"
\`\`\`

## Связь с другими концепциями

- **git show** — просмотр конкретного коммита
- **git blame** — кто изменил строку
- **git bisect** — поиск коммита с багом
- **git reflog** — история операций (включая потерянные коммиты)
- **git diff** — сравнение коммитов
`,
      steps: [
        {
          id: 1,
          title: 'Просмотр лога коммитов',
          description: 'Отобразите историю коммитов.',
          expectedCommand: 'git log',
          hint: {
            level1: 'Вы хотите увидеть все коммиты.',
            level2: 'Используйте git log.',
            level3: 'Введите: git log',
          },
        },
      ],
      keyInsight: {
        main: 'Git log имеет много опций для фильтрации и форматирования. Изучите самые полезные!',
        example:
          'git log --oneline --graph --all показывает компактную историю всех веток с визуализацией слияний. git log --since="2 weeks ago" --author="Иван" показывает коммиты Ивана за последние 2 недели.',
        warning:
          'В большом проекте git log может показать тысячи коммитов. Используйте -n для ограничения количества или фильтры по дате/автору.',
        tip: 'Создайте алиас для часто используемого формата: git config --global alias.lg "log --oneline --graph --all". Теперь git lg покажет красивую историю.',
        additional: [
          'git log -p показывает diff для каждого коммита.',
          'git log --stat показывает статистику изменённых файлов.',
          'git log -S "текст" ищет коммиты, добавившие или удалившие строку.',
        ],
      },
      estimatedTime: 10,
      quizQuestions: [
        {
          id: '2-2-q1',
          question: 'Какая команда показывает историю в одну строку на коммит?',
          options: [
            {
              id: 'a',
              text: 'git log --short',
              isCorrect: false,
              explanation: 'Неправильно. Флага --short нет в git log.',
            },
            {
              id: 'b',
              text: 'git log --oneline',
              isCorrect: true,
              explanation: 'Правильно! --oneline показывает каждый коммит в одну строку.',
            },
            {
              id: 'c',
              text: 'git log --compact',
              isCorrect: false,
              explanation: 'Неправильно. Флага --compact нет в git log.',
            },
            {
              id: 'd',
              text: 'git log --brief',
              isCorrect: false,
              explanation: 'Неправильно. Флага --brief нет в git log.',
            },
          ],
          explanation:
            'git log --oneline показывает сокращённый хеш и сообщение коммита в одну строку. Это удобно для быстрого просмотра истории.',
          difficulty: 'easy',
        },
        {
          id: '2-2-q2',
          question: 'Как найти коммиты, добавившие или удалившие определённую строку?',
          options: [
            {
              id: 'a',
              text: 'git log --grep="строка"',
              isCorrect: false,
              explanation: 'Неправильно. --grep ищет в сообщениях коммитов, а не в содержимом.',
            },
            {
              id: 'b',
              text: 'git log -S "строка"',
              isCorrect: true,
              explanation:
                'Правильно! -S (pickaxe) ищет коммиты, изменившие количество вхождений строки.',
            },
            {
              id: 'c',
              text: 'git log --find="строка"',
              isCorrect: false,
              explanation: 'Неправильно. Флага --find нет в git log.',
            },
            {
              id: 'd',
              text: 'git log --search="строка"',
              isCorrect: false,
              explanation: 'Неправильно. Флага --search нет в git log.',
            },
          ],
          explanation:
            'git log -S "строка" (pickaxe) находит коммиты, где количество вхождений строки изменилось. Это полезно для поиска, когда появился или исчез код.',
          difficulty: 'medium',
        },
        {
          id: '2-2-q3',
          question: 'Какая команда показывает, кто изменил каждую строку файла?',
          options: [
            {
              id: 'a',
              text: 'git log --author filename',
              isCorrect: false,
              explanation: 'Неправильно. Это покажет коммиты автора, а не кто изменил строки.',
            },
            {
              id: 'b',
              text: 'git blame filename',
              isCorrect: true,
              explanation: 'Правильно! git blame показывает, кто и когда изменил каждую строку.',
            },
            {
              id: 'c',
              text: 'git who filename',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
            {
              id: 'd',
              text: 'git history filename',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
          ],
          explanation:
            'git blame показывает для каждой строки файла: хеш коммита, автора, дату и содержимое строки. Это полезно для понимания истории изменений.',
          difficulty: 'easy',
        },
        {
          id: '2-2-q4',
          question: 'Как посмотреть историю конкретного файла, включая переименования?',
          options: [
            {
              id: 'a',
              text: 'git log filename',
              isCorrect: false,
              explanation: 'Неправильно. Это не отслеживает переименования.',
            },
            {
              id: 'b',
              text: 'git log --follow filename',
              isCorrect: true,
              explanation: 'Правильно! --follow отслеживает переименования файла.',
            },
            {
              id: 'c',
              text: 'git log --track filename',
              isCorrect: false,
              explanation: 'Неправильно. Флага --track нет.',
            },
            {
              id: 'd',
              text: 'git log --rename filename',
              isCorrect: false,
              explanation: 'Неправильно. Флага --rename нет.',
            },
          ],
          explanation:
            'git log --follow filename отслеживает переименования файла и показывает полную историю, даже если файл был переименован.',
          difficulty: 'medium',
        },
      ],
      resources: [
        {
          title: 'Git Documentation: git log',
          url: 'https://git-scm.com/docs/git-log',
          type: 'documentation',
          description: 'Официальная документация о команде log',
        },
        {
          title: 'Pretty Git Log Formats',
          url: 'https://devhints.io/git-log',
          type: 'article',
          description: 'Шпаргалка по форматированию вывода git log',
        },
        {
          title: 'Git Log Tips & Tricks',
          url: 'https://www.youtube.com/watch?v=U8GBXvdmHT4',
          type: 'video',
          description: 'Видео о продвинутых возможностях git log',
        },
      ],
    },
    {
      id: '2-3',
      moduleId: 'module-2',
      title: 'Игнорирование файлов',
      slug: 'ignoring-files',
      order: 3,
      theory: `# Игнорирование файлов

Не все файлы должны отслеживаться Git. Файл .gitignore позволяет указать, какие файлы игнорировать.

:::diagram gitignore-flow:::

## История концепции игнорирования

### Эволюция подходов:

| Система | Подход | Проблема |
|---------|--------|----------|
| RCS | Явное указание файлов | Неудобно |
| CVS | .cvsignore | Ограниченный синтаксис |
| Subversion | svn:ignore property | Сложно настраивать |
| **Git** | **.gitignore** | **Гибкий, мощный** |

### Почему игнорирование важно?

1. **Чистота истории** — не засорять репозиторий артефактами
2. **Безопасность** — не коммитить секреты и ключи
3. **Размер репозитория** — не хранить бинарники и зависимости
4. **Конфликты** — избегать конфликтов в IDE-файлах

## Что обычно игнорируют?

### Категории игнорируемых файлов:

| Категория | Примеры | Почему игнорировать |
|-----------|---------|---------------------|
| **Сборка** | \`dist/\`, \`build/\`, \`*.o\`, \`*.class\` | Генерируются автоматически |
| **Зависимости** | \`node_modules/\`, \`vendor/\`, \`venv/\` | Слишком большие, восстанавливаются |
| **IDE** | \`.idea/\`, \`.vscode/\`, \`*.swp\` | Личные настройки разработчика |
| **ОС** | \`.DS_Store\`, \`Thumbs.db\`, \`desktop.ini\` | Системные файлы, не относятся к проекту |
| **Секреты** | \`.env\`, \`*.pem\`, \`credentials.json\` | Безопасность |
| **Логи** | \`*.log\`, \`logs/\` | Временные данные |
| **Кэш** | \`.cache/\`, \`.npm/\`, \`__pycache__/\` | Восстанавливается |

## Глубокое погружение: Синтаксис .gitignore

### Базовые паттерны

\`\`\`bash
# Игнорировать все .log файлы
*.log

# Игнорировать директорию (слэш в конце — опционально, но рекомендуется)
node_modules/

# Игнорировать файл в корне репозитория
/.env

# Игнорировать файл в любой директории
config.local

# Игнорировать все файлы в директории
build/**
\`\`\`

### Продвинутые паттерны

\`\`\`bash
# Отрицание (исключение из игнорирования)
!important.log          # Не игнорировать important.log, даже если *.log игнорируется
!build/important.txt    # Не игнорировать конкретный файл

# Игнорировать только в корне
/README.md              # Только README.md в корне, не в поддиректориях

# Игнорировать по расширению в конкретной директории
docs/*.pdf              # PDF файлы только в docs/

# Двойная звезда (любая вложенность)
src/**/*.test.ts        # Все .test.ts файлы в src и поддиректориях

# Игнорировать всё, кроме определённых файлов
/*
!.gitignore
!src/
!README.md
\`\`\`

### Приоритет правил

\`\`\`
1. Паттерны с отрицанием (!) имеют приоритет
2. Более специфичные паттерны приоритетнее общих
3. Порядок в файле важен — последние правила могут переопределять
\`\`\`

## Уровни .gitignore

### 1. Репозиторий (.gitignore в корне)

\`\`\`bash
# Типичный .gitignore для Node.js проекта

# Зависимости
node_modules/

# Сборка
dist/
build/

# Окружение
.env
.env.local
.env.*.local

# Логи
logs/
*.log
npm-debug.log*

# IDE
.idea/
.vscode/
*.swp
*.swo

# ОС
.DS_Store
Thumbs.db

# Тесты
coverage/
.nyc_output/
\`\`\`

### 2. Поддиректории (.gitignore в подпапках)

\`\`\`bash
# В директории docs/ можно иметь свой .gitignore
*.pdf
*.docx
\`\`\`

### 3. Глобальный (~/.gitignore_global)

\`\`\`bash
# Настроить глобальный gitignore
git config --global core.excludesfile ~/.gitignore_global

# Типичное содержимое:
.idea/
.vscode/
.DS_Store
Thumbs.db
*.swp
*.swo
*~
\`\`\`

### 4. Внутрирепозиторий (.git/info/exclude)

\`\`\`bash
# Локальные правила, не коммитятся
# .git/info/exclude
# Формат такой же как .gitignore
my-local-config.json
\`\`\`

## Проверка игнорирования

\`\`\`bash
# Показать игнорируемые файлы
git status --ignored

# Проверить, почему файл игнорируется
git check-ignore -v filename.txt

# Пример вывода:
# .gitignore:3:*.log    debug.log
\`\`\`

### Отладка .gitignore

\`\`\`bash
# Показать все игнорируемые файлы
git ls-files --others --ignored --exclude-standard

# Проверить конкретный файл
git check-ignore -v src/file.log

# Показать, какие правила применяются
git check-ignore -v --stdin < file.txt
\`\`\`

## Если файл уже отслеживается

Важно: **.gitignore применяется только к неотслеживаемым файлам!**

\`\`\`bash
# Если файл уже в репозитории, нужно сначала удалить из отслеживания
git rm --cached filename.txt

# Для директории
git rm -r --cached directory/

# Затем добавить в .gitignore
echo "filename.txt" >> .gitignore

# Закоммитить изменения
git add .gitignore
git commit -m "Прекратить отслеживание filename.txt"
\`\`\`

### Массовое удаление отслеживания

\`\`\`bash
# Удалить из отслеживания всё, что теперь в .gitignore
git rm -r --cached .
git add .
git commit -m "Применить новые правила .gitignore"
\`\`\`

## Практические кейсы

### Кейс 1: "Случайно закоммитил .env с секретами"

\`\`\`bash
# 1. Немедленно удалить из истории
git rm --cached .env

# 2. Добавить в .gitignore
echo ".env" >> .gitignore

# 3. Закоммитить
git add .gitignore
git commit -m "Удалить .env из отслеживания"

# 4. Если секреты были в публичном репозитории — СРОЧНО МЕНЯЙТЕ!
# Даже после удаления они остаются в истории
\`\`\`

### Кейс 2: "Хочу игнорировать всё, кроме определённых файлов"

\`\`\`bash
# .gitignore
/*
!.gitignore
!src/
!README.md
!package.json

# Игнорировать всё в корне, кроме перечисленного
\`\`\`

### Кейс 3: "Игнорировать файлы, но сохранить структуру директорий"

\`\`\`bash
# Сохранить пустую директорию в Git
# Git не отслеживает пустые директории!

# Решение: добавить .gitkeep файл
mkdir logs
touch logs/.gitkeep

# .gitignore
logs/*
!logs/.gitkeep
\`\`\`

### Кейс 4: "Разные .gitignore для разных веток"

\`\`\`bash
# Используйте .git/info/exclude для локальных правил
# Или создайте скрипт для переключения конфигураций
\`\`\`

## Типичные ошибки

### Ошибка: "Файл в .gitignore, но Git всё равно видит его"

\`\`\`bash
# Причина: файл уже отслеживается
# Решение:
git rm --cached filename.txt
\`\`\`

### Ошибка: "Игнорируются файлы, которые не должны"

\`\`\`bash
# Отладка:
git check-ignore -v filename.txt

# Проверьте все .gitignore файлы:
find . -name ".gitignore"
\`\`\`

### Ошибка: "Не работает отрицание (!)"

\`\`\`bash
# Проблема: родительская директория игнорируется
build/           # Это игнорирует всю директорию
!build/important.txt  # Не работает!

# Решение:
build/*
!build/important.txt
\`\`\`

## Шаблоны .gitignore

### Популярные ресурсы:

\`\`\`bash
# Генератор .gitignore
https://www.toptal.com/developers/gitignore

# Коллекция GitHub
https://github.com/github/gitignore

# Примеры для популярных стеков:
\`\`\`

### Node.js + TypeScript

\`\`\`bash
# Dependencies
node_modules/

# Build
dist/
build/
*.tsbuildinfo

# Environment
.env
.env.local
.env.*.local

# Logs
logs/
*.log
npm-debug.log*

# IDE
.idea/
.vscode/
*.swp

# OS
.DS_Store
Thumbs.db

# Test
coverage/
\`\`\`

### Python

\`\`\`bash
# Byte-compiled
__pycache__/
*.py[cod]

# Virtual environments
venv/
.venv/
env/

# Distribution
dist/
build/
*.egg-info/

# Testing
.pytest_cache/
.coverage
htmlcov/

# IDE
.idea/
.vscode/
*.swp
\`\`\`

## Связь с другими концепциями

- **git clean** — удалить игнорируемые файлы
- **git add -f** — принудительно добавить игнорируемый файл
- **git ls-files** — показать отслеживаемые файлы
- **.gitattributes** — дополнительные атрибуты файлов
`,
      steps: [
        {
          id: 1,
          title: 'Проверка статуса',
          description: 'Проверьте текущее состояние репозитория.',
          expectedCommand: 'git status',
          hint: {
            level1: 'Проверьте состояние репозитория.',
            level2: 'Используйте git status.',
            level3: 'Введите: git status',
          },
        },
      ],
      keyInsight: {
        main: '.gitignore — важный файл для любого проекта. Добавляйте его в репозиторий!',
        example:
          'Типичный .gitignore для Node.js проекта: node_modules/, dist/, .env, *.log, .DS_Store. Это предотвратит попадание зависимостей, сборки и секретов в репозиторий.',
        warning:
          'Если файл уже отслеживается Git, добавление в .gitignore не остановит отслеживание! Сначала выполните git rm --cached <file>, затем добавьте в .gitignore.',
        tip: 'Используйте gitignore.io для генерации .gitignore под ваш стек технологий. Просто введите "Node, React, VSCode" и получите готовый файл.',
        additional: [
          '.gitignore должен быть в корне репозитория или в поддиректориях для локальных правил.',
          'Глобальный .gitignore настраивается через git config --global core.excludesfile.',
          'Проверить, почему файл игнорируется: git check-ignore -v filename.',
        ],
      },
      estimatedTime: 10,
      quizQuestions: [
        {
          id: '2-3-q1',
          question: 'Что произойдёт, если добавить уже отслеживаемый файл в .gitignore?',
          options: [
            {
              id: 'a',
              text: 'Git перестанет отслеживать файл',
              isCorrect: false,
              explanation: 'Неправильно. .gitignore работает только для неотслеживаемых файлов.',
            },
            {
              id: 'b',
              text: 'Git продолжит отслеживать файл',
              isCorrect: true,
              explanation: 'Правильно! .gitignore не влияет на уже отслеживаемые файлы.',
            },
            {
              id: 'c',
              text: 'Git удалит файл из репозитория',
              isCorrect: false,
              explanation: 'Неправильно. Git не удаляет файлы автоматически.',
            },
            {
              id: 'd',
              text: 'Git выдаст ошибку',
              isCorrect: false,
              explanation: 'Неправильно. Git просто проигнорирует правило для этого файла.',
            },
          ],
          explanation:
            '.gitignore применяется только к неотслеживаемым файлам. Чтобы прекратить отслеживание, нужно выполнить git rm --cached filename.',
          difficulty: 'medium',
        },
        {
          id: '2-3-q2',
          question: 'Какая команда проверяет, почему файл игнорируется?',
          options: [
            {
              id: 'a',
              text: 'git status --ignored',
              isCorrect: false,
              explanation:
                'Неправильно. Это показывает игнорируемые файлы, но не объясняет почему.',
            },
            {
              id: 'b',
              text: 'git check-ignore -v filename',
              isCorrect: true,
              explanation:
                'Правильно! -v показывает, какое правило в каком файле вызывает игнорирование.',
            },
            {
              id: 'c',
              text: 'git ignore filename',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
            {
              id: 'd',
              text: 'git ls-files --ignored',
              isCorrect: false,
              explanation:
                'Неправильно. Это показывает игнорируемые файлы, но не объясняет почему.',
            },
          ],
          explanation:
            'git check-ignore -v filename показывает, какой файл .gitignore и какое правило вызывают игнорирование файла. Это полезно для отладки.',
          difficulty: 'medium',
        },
        {
          id: '2-3-q3',
          question: 'Какой паттерн в .gitignore игнорирует все .log файлы, кроме important.log?',
          options: [
            {
              id: 'a',
              text: '*.log\n!important.log',
              isCorrect: true,
              explanation:
                'Правильно! Сначала игнорируем все .log, затем делаем исключение для important.log.',
            },
            {
              id: 'b',
              text: '!*.log\nimportant.log',
              isCorrect: false,
              explanation:
                'Неправильно. Порядок правил важен — сначала нужно игнорировать, потом исключать.',
            },
            {
              id: 'c',
              text: '*.log --except important.log',
              isCorrect: false,
              explanation: 'Неправильно. Такого синтаксиса нет.',
            },
            {
              id: 'd',
              text: 'important.log\n!*.log',
              isCorrect: false,
              explanation: 'Неправильно. Порядок и логика неправильные.',
            },
          ],
          explanation:
            'В .gitignore можно использовать отрицание (!) для исключения из правил. Порядок важен: сначала игнорируем, потом исключаем.',
          difficulty: 'medium',
        },
        {
          id: '2-3-q4',
          question: 'Как прекратить отслеживание файла, не удаляя его из рабочей директории?',
          options: [
            {
              id: 'a',
              text: 'git rm filename',
              isCorrect: false,
              explanation: 'Неправильно. git rm удаляет файл из рабочей директории тоже.',
            },
            {
              id: 'b',
              text: 'git rm --cached filename',
              isCorrect: true,
              explanation: 'Правильно! --cached удаляет только из индекса, сохраняя файл локально.',
            },
            {
              id: 'c',
              text: 'git untrack filename',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
            {
              id: 'd',
              text: 'git ignore filename',
              isCorrect: false,
              explanation: 'Неправильно. Такой команды нет.',
            },
          ],
          explanation:
            'git rm --cached filename удаляет файл из индекса (staging area), но сохраняет его в рабочей директории. После этого файл можно добавить в .gitignore.',
          difficulty: 'easy',
        },
      ],
      resources: [
        {
          title: 'gitignore.io',
          url: 'https://www.toptal.com/developers/gitignore',
          type: 'tool',
          description: 'Генератор .gitignore файлов для разных языков и IDE',
        },
        {
          title: 'GitHub gitignore Templates',
          url: 'https://github.com/github/gitignore',
          type: 'article',
          description: 'Коллекция шаблонов .gitignore от GitHub',
        },
        {
          title: 'Git Documentation: gitignore',
          url: 'https://git-scm.com/docs/gitignore',
          type: 'documentation',
          description: 'Официальная документация о формате .gitignore',
        },
      ],
    },
    {
      id: '2-4',
      moduleId: 'module-2',
      title: 'Просмотр изменений',
      slug: 'viewing-changes',
      order: 4,
      theory: `# Просмотр изменений

Git diff позволяет увидеть, что именно изменилось в файлах.

:::diagram diff-flow:::

## История концепции diff

### Эволюция инструментов сравнения:

| Система | Инструмент | Особенности |
|---------|------------|-------------|
| RCS | rcsdiff | Только локальные файлы |
| CVS | cvs diff | Требует сети |
| Subversion | svn diff | Медленный, централизованный |
| **Git** | **git diff** | **Локальный, быстрый, гибкий** |

### Почему git diff важен?

1. **Проверка перед коммитом** — убедиться, что попадёт в коммит
2. **Code review** — понять изменения до ревью
3. **Отладка** — найти, когда появилась ошибка
4. **Документация** — показать изменения в запросе

## Базовый diff

### Три уровня сравнения

\`\`\`
Рабочая директория ←── git diff ──→ Индекс ←── git diff --staged ──→ HEAD
\`\`\`

\`\`\`bash
# Изменения в рабочей директории (не в индексе)
git diff

# Изменения в индексе (готовые к коммиту)
git diff --staged
# или (синоним)
git diff --cached

# Все незафиксированные изменения
git diff HEAD
\`\`\`

### Пример вывода git diff

\`\`\`bash
$ git diff
diff --git a/src/auth.ts b/src/auth.ts
index a1b2c3d..e4f5g6h 100644
--- a/src/auth.ts
+++ b/src/auth.ts
@@ -10,6 +10,8 @@ function login(user) {
    const token = generateToken(user);
+  console.log('Debug: token', token);  // Отладка
    saveSession(token);
+  // TODO: добавить валидацию
    return token;
  }
\`\`\`

### Разбор вывода:

\`\`\`
diff --git a/src/auth.ts b/src/auth.ts    # Файлы для сравнения
index a1b2c3d..e4f5g6h 100644              # Хеши и режим файла
--- a/src/auth.ts                          # Исходный файл (минус)
+++ b/src/auth.ts                          # Изменённый файл (плюс)
@@ -10,6 +10,8 @@                          # Контекст: строка 10, 6→8 строк
\`\`\`

## Глубокое погружение: Формат unified diff

### Структура hunk (блока изменений)

\`\`\`
@@ -start,count +start,count @@ optional_heading
\`\`\`

- **-start,count** — позиция в исходном файле
- **+start,count** — позиция в изменённом файле
- **count** — количество строк (опускается если = 1)

### Пример с объяснением:

\`\`\`
@@ -1,5 +1,7 @@            # Hunk header
 existing line 1           # Контекст (пробел в начале)
-existing line 2           # Удалённая строка (минус)
+new line 2                # Добавленная строка (плюс)
+new line 3                # Добавленная строка (плюс)
 existing line 4           # Контекст
-existing line 5           # Удалённая
+modified line 5           # Изменённая
\`\`\`

### Цветовая кодировка:

| Цвет | Значение |
|------|----------|
| Зелёный | Добавленные строки |
| Красный | Удалённые строки |
| Белый/без цвета | Контекст (не изменён) |
| Жёлтый | Заголовок hunk |

## Сравнение веток и коммитов

\`\`\`bash
# Разница между ветками
git diff main..feature-branch

# Разница между коммитами
git diff abc123..def456

# Разница с общим предком (трёхточечный синтаксис)
git diff main...feature-branch  # От общего предка до feature-branch

# Только имена изменённых файлов
git diff --name-only main..feature-branch

# Имена со статусом (A=added, M=modified, D=deleted)
git diff --name-status main..feature-branch

# Статистика изменений
git diff --stat main..feature-branch
\`\`\`

### Разница между .. и ...

\`\`\`
main:       A --- B --- C
                    \\
feature:            D --- E

git diff main..feature    # D, E (изменения в feature относительно main)
git diff main...feature   # D, E (то же самое в данном случае)

# Но если ветки разошлись:
main:       A --- B --- C --- F
                    \\
feature:            D --- E

git diff main..feature    # D, E, F (все различия)
git diff main...feature   # D, E (от общего предка B)
\`\`\`

## Сравнение файлов

\`\`\`bash
# Изменения в конкретном файле
git diff filename.txt

# Сравнение файла между ветками
git diff main..feature -- filename.txt

# Сравнение файла между коммитами
git diff abc123:def456 -- path/to/file.txt

# Сравнение конкретных версий файла
git diff HEAD~2:file.txt HEAD:file.txt
\`\`\`

## Продвинутые опции

### Игнорирование изменений

\`\`\`bash
# Игнорировать изменения в пробелах
git diff -w
git diff --ignore-all-space

# Игнорировать изменения в конце строк
git diff --ignore-space-at-eol

# Игнорировать пустые строки
git diff --ignore-blank-lines
\`\`\`

### Контекст и детализация

\`\`\`bash
# Показать N строк контекста вокруг изменений
git diff -U5              # 5 строк контекста
git diff -U0              # Без контекста

# Показать изменения по словам
git diff --word-diff

# Показать изменения по словам с цветом
git diff --word-diff=color

# Показать изменения в формате porcelain
git diff --porcelain
\`\`\`

### Статистика и сводки

\`\`\`bash
# Статистика изменений
git diff --stat

# Пример вывода:
#  src/auth.ts      | 15 +++++++++++++++
#  src/index.ts     |  3 ++-
#  2 files changed, 17 insertions(+), 1 deletion(-)

# Краткая статистика
git diff --shortstat

# Пример:
# 2 files changed, 17 insertions(+), 1 deletion(-)

# Только количества изменений
git diff --numstat

# Пример:
# 17    1    src/auth.ts
# 3     1    src/index.ts
\`\`\`

## Поиск в diff

\`\`\`bash
# Найти изменения, содержащие строку
git diff -S "function login"

# Найти изменения по регулярному выражению
git diff -G "function.*login"

# Показать только изменения, затрагивающие паттерн
git diff --pickaxe-all -S "password"
\`\`\`

## Сравнение с внешними инструментами

\`\`\`bash
# Использовать внешний diff-инструмент
git difftool

# Список доступных инструментов
git difftool --tool-help

# Использовать конкретный инструмент
git difftool --tool=meld
git difftool --tool=vimdiff

# Настроить инструмент по умолчанию
git config --global diff.tool meld
\`\`\`

### Популярные diff-инструменты:

| Инструмент | Платформа | Особенности |
|------------|-----------|-------------|
| Meld | Linux, Windows, Mac | GUI, трёхсторонний |
| Beyond Compare | Windows, Mac | Мощный, платный |
| KDiff3 | Linux, Windows, Mac | Бесплатный |
| vimdiff | Все | Встроен в Vim |
| VS Code | Все | \`code --diff file1 file2\` |

## Практические кейсы

### Кейс 1: "Проверка перед коммитом"

\`\`\`bash
# Посмотреть, что будет закоммичено
git diff --staged

# Посмотреть все незафиксированные изменения
git diff HEAD

# Проверить конкретный файл
git diff --staged -- src/auth.ts
\`\`\`

### Кейс 2: "Найти, когда появилась ошибка"

\`\`\`bash
# Сравнить с предыдущей версией
git diff HEAD~1 HEAD -- src/auth.ts

# Найти коммит, добавивший строку
git log -S "buggyFunction" -p

# Сравнить с тегом релиза
git diff v1.0.0 HEAD -- src/auth.ts
\`\`\`

### Кейс 3: "Просмотр изменений в файле за период"

\`\`\`bash
# Изменения за последнюю неделю
git diff HEAD@{1.week.ago} HEAD -- filename.txt

# Изменения между коммитами
git log --oneline -- filename.txt
git diff <commit1> <commit2> -- filename.txt
\`\`\`

### Кейс 4: "Сравнение веток перед слиянием"

\`\`\`bash
# Посмотреть все изменения в feature-ветке
git diff main...feature

# Только файлы, которые изменились
git diff --name-status main...feature

# Статистика изменений
git diff --stat main...feature
\`\`\`

### Кейс 5: "Экспорт diff в файл"

\`\`\`bash
# Сохранить diff в файл
git diff > changes.patch

# Применить патч в другом репозитории
git apply changes.patch

# Создать патч для коммита
git format-patch -1 HEAD
\`\`\`

## Типичные ошибки

### Ошибка: "Не вижу изменений в git diff"

\`\`\`bash
# Проблема: изменения в индексе, а не в рабочей директории
git diff                    # Пусто

# Решение: проверить staged изменения
git diff --staged           # Показывает изменения
\`\`\`

### Ошибка: "Слишком много шума в diff"

\`\`\`bash
# Проблема: много изменений в пробелах
git diff

# Решение: игнорировать пробелы
git diff -w
git diff --ignore-all-space
\`\`\`

### Ошибка: "Неправильное сравнение веток"

\`\`\`bash
# Проблема: используем .. вместо ...
git diff main..feature      # Включает изменения в main

# Решение: использовать ... для сравнения от общего предка
git diff main...feature     # Только изменения в feature
\`\`\`

### Ошибка: "Diff показывает удаление и добавление вместо изменения"

\`\`\`bash
# Проблема: Git не распознал переименование/перемещение
git diff                   # Показывает -old +new

# Решение: включить обнаружение переименований
git diff -M                # Показывает rename
git diff -M90              # Порог схожести 90%
\`\`\`

## Полезные алиасы

\`\`\`bash
# Быстрый просмотр staged
git config --global alias.ds "diff --staged"

# Компактный diff
git config --global alias.dc "diff --compact"

# Diff с игнорированием пробелов
git config --global alias.dw "diff -w"

# Статистика diff
git config --global alias.ds "diff --stat"
\`\`\`

## Связь с другими концепциями

- **git add -p** — интерактивное добавление на основе diff
- **git log -p** — история с diff'ами
- **git show** — показать коммит с diff
- **git stash show -p** — показать спрятанные изменения
- **git apply** — применить patch-файл
`,
      steps: [
        {
          id: 1,
          title: 'Просмотр изменений',
          description: 'Посмотрите, какие изменения есть в рабочей директории.',
          expectedCommand: 'git diff',
          hint: {
            level1: 'Вы хотите увидеть изменения в файлах.',
            level2: 'Используйте git diff.',
            level3: 'Введите: git diff',
          },
        },
      ],
      keyInsight: {
        main: 'Git diff — мощный инструмент для понимания изменений. Используйте его перед каждым коммитом!',
        example:
          'Перед коммитом всегда выполняйте git diff --staged чтобы увидеть, что именно будет сохранено. Это поможет поймать ошибки, забытые отладочные сообщения и лишние изменения.',
        warning:
          'git diff без флагов показывает только изменения в Working Directory, не в индексе. Не забудьте git diff --staged для изменений, готовых к коммиту.',
        tip: 'git diff --word-diff показывает изменения по словам, что полезно для текстовых файлов. git diff --color-words выделяет изменения цветом.',
        additional: [
          'git diff HEAD показывает все незафиксированные изменения.',
          'git diff branch1..branch2 сравнивает две ветки.',
          'git diff --name-only показывает только имена изменённых файлов.',
        ],
      },
      estimatedTime: 15,
      quizQuestions: [
        {
          id: '2-4-q1',
          question: 'Что показывает команда git diff без аргументов?',
          options: [
            {
              id: 'a',
              text: 'Изменения в индексе (staged)',
              isCorrect: false,
              explanation: 'Неправильно. Для этого нужен git diff --staged.',
            },
            {
              id: 'b',
              text: 'Изменения в рабочей директории (unstaged)',
              isCorrect: true,
              explanation:
                'Правильно! git diff показывает изменения между рабочей директорией и индексом.',
            },
            {
              id: 'c',
              text: 'Все незафиксированные изменения',
              isCorrect: false,
              explanation: 'Неправильно. Для этого нужен git diff HEAD.',
            },
            {
              id: 'd',
              text: 'Изменения в последнем коммите',
              isCorrect: false,
              explanation: 'Неправильно. Для этого нужен git show или git diff HEAD~1 HEAD.',
            },
          ],
          explanation:
            'git diff без аргументов показывает изменения между рабочей директорией и индексом (staging area). Это unstaged изменения.',
          difficulty: 'easy',
        },
        {
          id: '2-4-q2',
          question: 'Как посмотреть все незафиксированные изменения (и staged, и unstaged)?',
          options: [
            {
              id: 'a',
              text: 'git diff --all',
              isCorrect: false,
              explanation: 'Неправильно. Флага --all нет в git diff.',
            },
            {
              id: 'b',
              text: 'git diff HEAD',
              isCorrect: true,
              explanation:
                'Правильно! git diff HEAD сравнивает рабочую директорию с последним коммитом.',
            },
            {
              id: 'c',
              text: 'git diff --staged',
              isCorrect: false,
              explanation: 'Неправильно. Это показывает только staged изменения.',
            },
            {
              id: 'd',
              text: 'git diff --total',
              isCorrect: false,
              explanation: 'Неправильно. Флага --total нет.',
            },
          ],
          explanation:
            'git diff HEAD сравнивает текущее состояние (рабочая директория + индекс) с последним коммитом, показывая все незафиксированные изменения.',
          difficulty: 'medium',
        },
        {
          id: '2-4-q3',
          question: 'В чём разница между git diff main..feature и git diff main...feature?',
          options: [
            {
              id: 'a',
              text: 'Нет разницы',
              isCorrect: false,
              explanation: 'Неправильно. Разница есть.',
            },
            {
              id: 'b',
              text: '.. сравнивает концы веток, ... сравнивает от общего предка',
              isCorrect: true,
              explanation: 'Правильно! ... находит общего предка и сравнивает от него.',
            },
            {
              id: 'c',
              text: '.. для локальных веток, ... для удалённых',
              isCorrect: false,
              explanation: 'Неправильно. Это не зависит от типа веток.',
            },
            {
              id: 'd',
              text: '.. показывает имена файлов, ... показывает diff',
              isCorrect: false,
              explanation: 'Неправильно. Оба показывают diff.',
            },
          ],
          explanation:
            'git diff main..feature сравнивает концы веток. git diff main...feature находит общего предка и сравнивает от него до feature. Это полезно для понимания изменений только в feature ветке.',
          difficulty: 'hard',
        },
        {
          id: '2-4-q4',
          question: 'Как игнорировать изменения в пробелах при сравнении?',
          options: [
            {
              id: 'a',
              text: 'git diff --no-spaces',
              isCorrect: false,
              explanation: 'Неправильно. Такого флага нет.',
            },
            {
              id: 'b',
              text: 'git diff -w или --ignore-all-space',
              isCorrect: true,
              explanation: 'Правильно! -w игнорирует все изменения в пробелах.',
            },
            {
              id: 'c',
              text: 'git diff --trim',
              isCorrect: false,
              explanation: 'Неправильно. Такого флага нет.',
            },
            {
              id: 'd',
              text: 'git diff --clean',
              isCorrect: false,
              explanation: 'Неправильно. Такого флага нет.',
            },
          ],
          explanation:
            'git diff -w (или --ignore-all-space) игнорирует изменения в пробелах. Это полезно, когда изменения в форматировании засоряют diff.',
          difficulty: 'medium',
        },
      ],
      resources: [
        {
          title: 'Git Documentation: git diff',
          url: 'https://git-scm.com/docs/git-diff',
          type: 'documentation',
          description: 'Официальная документация о команде diff',
        },
        {
          title: 'Comparing Changes in Git',
          url: 'https://www.atlassian.com/git/tutorials/saving-changes/git-diff',
          type: 'article',
          description: 'Туториал Atlassian о сравнении изменений',
        },
        {
          title: 'Git Diff Tools',
          url: 'https://www.gitkraken.com/learn/git/git-diff',
          type: 'article',
          description: 'Обзор инструментов для сравнения в Git',
        },
      ],
    },
  ],
};

export { module2 };
